import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBriefcase, FiMail, FiPhone, FiMapPin, FiGlobe, FiUpload, FiSave, FiPlus, FiTrash2, FiBookOpen, FiDollarSign, FiUsers } = FiIcons;

const CompanyProfile = () => {
  const { user, updateProfile } = useAuth();
  const { jobs, addJob, internships, addInternship } = useData();
  const [formData, setFormData] = useState({
    companyName: user.companyName || '',
    contactPerson: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    industry: user.industry || '',
    description: user.description || '',
    boothNumber: user.boothNumber || '',
    website: user.website || ''
  });

  const [jobForm, setJobForm] = useState({
    title: '',
    department: '',
    level: '',
    location: '',
    description: ''
  });

  const [internshipForm, setInternshipForm] = useState({
    title: '',
    positions: '',
    isPaid: 'paid',
    skillsOffered: '',
    description: ''
  });

  const [showJobForm, setShowJobForm] = useState(false);
  const [showInternshipForm, setShowInternshipForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const companyJobs = jobs.filter(job => job.companyId === user.id);
  const companyInternships = internships.filter(int => int.companyId === user.id);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleJobChange = (e) => {
    setJobForm({ ...jobForm, [e.target.name]: e.target.value });
  };

  const handleInternshipChange = (e) => {
    setInternshipForm({ ...internshipForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    updateProfile(formData);
    setSuccess(true);
    setLoading(false);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleAddJob = (e) => {
    e.preventDefault();
    addJob({ ...jobForm, companyId: user.id });
    setJobForm({ title: '', department: '', level: '', location: '', description: '' });
    setShowJobForm(false);
  };

  const handleAddInternship = (e) => {
    e.preventDefault();
    addInternship({ 
      ...internshipForm, 
      companyId: user.id,
      isPaid: internshipForm.isPaid === 'paid'
    });
    setInternshipForm({ title: '', positions: '', isPaid: 'paid', skillsOffered: '', description: '' });
    setShowInternshipForm(false);
  };

  const industries = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Consulting', 'Media & Entertainment', 'Non-profit', 'Government'
  ];

  const levels = ['Intern', 'Junior', 'Mid', 'Senior', 'Lead', 'Principal'];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Profile</h1>
        <p className="text-gray-600">
          Complete your company profile and add job openings for the job fair.
        </p>
      </motion.div>

      {success && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-6"
        >
          Profile updated successfully!
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Company Profile Form */}
        <div className="lg:col-span-2">
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Company Information</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiBriefcase} className="text-gray-400" />
                    </div>
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your company name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    id="contactPerson"
                    name="contactPerson"
                    type="text"
                    required
                    value={formData.contactPerson}
                    onChange={handleChange}
                    className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact person name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="company@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiPhone} className="text-gray-400" />
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    required
                    value={formData.industry}
                    onChange={handleChange}
                    className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select industry...</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="boothNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Booth Number *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMapPin} className="text-gray-400" />
                    </div>
                    <input
                      id="boothNumber"
                      name="boothNumber"
                      type="text"
                      required
                      value={formData.boothNumber}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="A1, B2, C3, etc."
                    />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiGlobe} className="text-gray-400" />
                  </div>
                  <input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  required
                  value={formData.description}
                  onChange={handleChange}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your company, mission, and what makes it a great place to work..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <SafeIcon icon={FiUpload} className="text-gray-400 text-3xl mx-auto mb-2" />
                  <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-sm text-gray-500">PNG, JPG, SVG up to 2MB</p>
                  <input type="file" className="hidden" accept=".png, .jpg, .jpeg, .svg" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    <span>Save Company Profile</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Job Openings */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Job Openings</h2>
              <button 
                onClick={() => setShowJobForm(!showJobForm)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add Job</span>
              </button>
            </div>

            {showJobForm && (
              <form onSubmit={handleAddJob} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title *
                    </label>
                    <input
                      name="title"
                      type="text"
                      required
                      value={jobForm.title}
                      onChange={handleJobChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Software Engineer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department *
                    </label>
                    <input
                      name="department"
                      type="text"
                      required
                      value={jobForm.department}
                      onChange={handleJobChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Engineering"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Level *
                    </label>
                    <select
                      name="level"
                      required
                      value={jobForm.level}
                      onChange={handleJobChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select level...</option>
                      {levels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      name="location"
                      type="text"
                      required
                      value={jobForm.location}
                      onChange={handleJobChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., New York, Remote"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description *
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    required
                    value={jobForm.description}
                    onChange={handleJobChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the role, responsibilities, and requirements..."
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Add Job
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJobForm(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {companyJobs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No job openings added yet. Add your first job opening to attract candidates!
                </p>
              ) : (
                companyJobs.map((job) => (
                  <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{job.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span>{job.department}</span>
                          <span>•</span>
                          <span>{job.level}</span>
                          <span>•</span>
                          <span>{job.location}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{job.description}</p>
                      </div>
                      <button className="text-red-600 hover:text-red-700 transition-colors ml-4">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>

          {/* Internship Programs */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.25 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Internship Programs</h2>
              <button 
                onClick={() => setShowInternshipForm(!showInternshipForm)}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add Internship</span>
              </button>
            </div>

            {showInternshipForm && (
              <form onSubmit={handleAddInternship} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Program Title *
                    </label>
                    <input
                      name="title"
                      type="text"
                      required
                      value={internshipForm.title}
                      onChange={handleInternshipChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Summer Software Internship"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Positions *
                    </label>
                    <input
                      name="positions"
                      type="number"
                      required
                      min="1"
                      value={internshipForm.positions}
                      onChange={handleInternshipChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Compensation *
                    </label>
                    <select
                      name="isPaid"
                      required
                      value={internshipForm.isPaid}
                      onChange={handleInternshipChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="paid">Paid Internship</option>
                      <option value="unpaid">Unpaid Internship</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skills Offered *
                    </label>
                    <input
                      name="skillsOffered"
                      type="text"
                      required
                      value={internshipForm.skillsOffered}
                      onChange={handleInternshipChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., React, AI, Project Management"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Program Description *
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    required
                    value={internshipForm.description}
                    onChange={handleInternshipChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe what interns will learn and do..."
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Add Internship
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInternshipForm(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {companyInternships.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No internship programs added yet. Create opportunities for students!
                </p>
              ) : (
                companyInternships.map((internship) => (
                  <div key={internship.id} className="border border-purple-100 bg-purple-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="font-semibold text-gray-900">{internship.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${internship.isPaid ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            {internship.isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-2 mb-2">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUsers} className="text-purple-500" />
                            <span>{internship.positions} Positions</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiBookOpen} className="text-purple-500" />
                            <span>Skills: {internship.skillsOffered}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{internship.description}</p>
                      </div>
                      <button className="text-red-600 hover:text-red-700 transition-colors ml-4 bg-white p-2 rounded-lg shadow-sm">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* Company Summary */}
        <div className="space-y-6">
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Completion</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Company Info</span>
                <span className="text-green-600 font-medium">✓ Complete</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Contact Details</span>
                <span className="text-green-600 font-medium">✓ Complete</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Company Logo</span>
                <span className="text-yellow-600 font-medium">⚠ Pending</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Job Openings</span>
                <span className={companyJobs.length > 0 ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {companyJobs.length > 0 ? '✓ Complete' : '⚠ Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Internships</span>
                <span className={companyInternships.length > 0 ? "text-green-600 font-medium" : "text-gray-400"}>
                  {companyInternships.length > 0 ? '✓ Active' : 'Optional'}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Job Openings</span>
                <span className="font-semibold text-gray-900">{companyJobs.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Internships</span>
                <span className="font-semibold text-purple-600">{companyInternships.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Booth Number</span>
                <span className="font-semibold text-gray-900">{formData.boothNumber || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Industry</span>
                <span className="font-semibold text-gray-900">{formData.industry || 'Not set'}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;